import { Button, Checkbox, Input, message, Select } from "antd";
import React, { useState } from "react";

import "../../TreatmentPlans/component/Breakdown/breakdownAddProcedureForm.css";
import { controller } from "../../TreatmentPlans/controller";
import { describeApiError } from "../../TreatmentPlans/lib/apiError";

import arrowDown from "../../../assets/icons/arrow-down12.png";

export default function BreakdownAddProcedureForm({
  setShowAddProcedureFormRow,
  dropdownProceduresOptions,
  setNewBreakdownID,
  allProcedures,
  setTableData,
  breakdown,
  patient,
}) {
  const [selectedProcedure, setSelectedProcedure] = useState(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingNewProcedure, setIsCreatingNewProcedure] = useState(false);
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [isFrequencyUnlimited, setIsFrequencyUnlimited] = useState(false);
  const [formInputTextData, setFormInputTextData] = useState({
    percentage_covered: "",
    overall_max: "",
    max_occurrences: "",
    coverage_duration_months: "",
    notes: "",
  });

  // Handle Unlimited checkbox change for Maximum Amount
  const handleUnlimitedChange = (checked) => {
    setIsUnlimited(checked);
    setFormInputTextData((prevData) => ({
      ...prevData,
      overall_max: checked ? "9999999.99" : "",
    }));
  };

  // Handle Unlimited checkbox change for Frequency
  const handleFrequencyUnlimitedChange = (checked) => {
    setIsFrequencyUnlimited(checked);
    setFormInputTextData((prevData) => ({
      ...prevData,
      max_occurrences: checked ? "" : prevData.max_occurrences,
      coverage_duration_months: checked ? "999" : "",
    }));
  };

  // Validate that a value is a number with no more than two decimal places
  const isValidNumber = (value) => {
    if (!value || value === "") return true; // Allow empty inputs to be handled by required field validation
    if (isNaN(value)) return false;
    if (value.includes(".") && value.split(".")[1].length > 2) return false;
    return Number(value) >= 0;
  };

  // Handle input changes with proper number parsing
  const handleInputChange = (key, value) => {
    setFormInputTextData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  // Create a new breakdown procedure
  async function handleCreateNewBreakdownProcedure() {
    // NOTE: unlike the sibling form under TreatmentPlans/component/Breakdown,
    // this older copy never sends `insurance_policy` — which the create
    // serializer requires — so every save here is refused. The `else` added
    // below at least makes that say so out loud instead of doing nothing; it is
    // not a fix for the missing field.
    if (selectedProcedure === undefined) {
      message.error("Procedure Code can't be empty");
      return;
    }

    // Validate required fields (frequency is optional)
    if (
      !formInputTextData.percentage_covered ||
      (!isUnlimited && !formInputTextData.overall_max) ||
      !formInputTextData.notes
    ) {
      message.error("Please fill up all required fields");
      return;
    }

    // Validate percentage_covered
    if (
      formInputTextData.percentage_covered.toLowerCase() !== "unlimited" &&
      (!isValidNumber(formInputTextData.percentage_covered) ||
        Number(formInputTextData.percentage_covered) < 0 ||
        Number(formInputTextData.percentage_covered) > 100)
    ) {
      message.error("Coverage must be a number between 0 and 100 with no more than two decimal places.");
      return;
    }

    // Validate overall_max if not unlimited
    if (!isUnlimited && !isValidNumber(formInputTextData.overall_max)) {
      message.error("Maximum Amount must be a non-negative number with no more than two decimal places");
      return;
    }

    // Validate max_occurrences and coverage_duration_months only when both are filled (frequency is optional)
    if (!isFrequencyUnlimited) {
      const hasOccurrences = formInputTextData.max_occurrences !== "" && formInputTextData.max_occurrences !== undefined;
      const hasMonths = formInputTextData.coverage_duration_months !== "" && formInputTextData.coverage_duration_months !== undefined;
      if (hasOccurrences || hasMonths) {
        if (!isValidNumber(formInputTextData.max_occurrences) || !isValidNumber(formInputTextData.coverage_duration_months)) {
          message.error("Occurrences and months must be valid non-negative numbers when specified");
          return;
        }
      }
    }

    try {
      setIsCreatingNewProcedure(true);

      const hasFrequency =
        (formInputTextData.max_occurrences !== "" && formInputTextData.max_occurrences !== undefined) ||
        (formInputTextData.coverage_duration_months !== "" && formInputTextData.coverage_duration_months !== undefined);

      const response = await controller.addNewBreakdownProcedure({
        insurance_breakdown_id: breakdown !== null && breakdown !== undefined ? breakdown.id : null,
        patient_id: patient.id,
        procedure_code: selectedProcedure.id,
        percentage_covered: formInputTextData.percentage_covered,
        overall_max: isUnlimited ? "9999999.99" : formInputTextData.overall_max || "0",
        max_occurrences: isFrequencyUnlimited ? null : hasFrequency ? formInputTextData.max_occurrences : null,
        coverage_duration_months: isFrequencyUnlimited ? 999 : hasFrequency ? formInputTextData.coverage_duration_months : null,
        notes: formInputTextData.notes,
      });

      if (response.status < 250) {
        setTableData((prevData) => [...prevData, response.json]);
        setShowAddProcedureFormRow(false);
        setNewBreakdownID(response.json.insurance_breakdown);
      } else {
        message.error(describeApiError(response, "Couldn't add that procedure."));
      }
    } catch (err) {
      message.error("Something went wrong while adding procedure");
      console.error(err);
    } finally {
      setIsCreatingNewProcedure(false);
    }
  }

  return (
    <div className="breakdown-form-scroll-wrap">
      <div className="breakdown-form">
      {/* Procedure Code */}
      <div className="breakdown-form-procedure">
        <Select
          showSearch
          placeholder="Select Procedure"
          optionFilterProp="label"
          options={dropdownProceduresOptions}
          style={{ height: 39, width: "100%" }}
          value={
            selectedProcedure
              ? `${selectedProcedure.procedure_code} ${
                  selectedProcedure.procedure_code_description ? `- ${selectedProcedure.procedure_code_description}` : ""
                }`
              : undefined
          }
          suffixIcon={<img style={{ width: 20, transform: `rotate(${isOpen ? "180deg" : "0"})` }} src={arrowDown} alt="arrow-down" />}
          onOpenChange={(open) => setIsOpen(open)}
          onChange={(value) => setSelectedProcedure(allProcedures.find((procedure) => procedure.id === value))}
          disabled={isCreatingNewProcedure}
        />
      </div>

      {/* Coverage */}
      <div className="breakdown-form-coverage">
        <Input
          onChange={(e) => handleInputChange("percentage_covered", e.target.value)}
          placeholder="Enter Coverage"
          value={formInputTextData.percentage_covered}
          disabled={isCreatingNewProcedure}
        />
      </div>

      {/* Maximum Amount */}
      <div className="breakdown-form-overall-max">
        <Input
          onChange={(e) => handleInputChange("overall_max", e.target.value)}
          placeholder="Enter Maximum Amount"
          value={isUnlimited ? "Unlimited" : formInputTextData.overall_max}
          disabled={isCreatingNewProcedure || isUnlimited}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Checkbox checked={isUnlimited} onChange={(e) => handleUnlimitedChange(e.target.checked)} />
          Unlimited
        </label>
      </div>

      {/* Frequency */}
      <div className="breakdown-form-max-occurences">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Input
            onChange={(e) => handleInputChange("max_occurrences", e.target.value)}
            placeholder="Occurrences"
            style={{ width: 50 }}
            value={formInputTextData.max_occurrences}
            disabled={isCreatingNewProcedure || isFrequencyUnlimited}
          />
          <span>every</span>
          <Input
            onChange={(e) => handleInputChange("coverage_duration_months", e.target.value)}
            placeholder="Months"
            style={{ width: 50 }}
            value={formInputTextData.coverage_duration_months}
            disabled={isCreatingNewProcedure || isFrequencyUnlimited}
          />
          <span>months</span>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Checkbox checked={isFrequencyUnlimited} onChange={(e) => handleFrequencyUnlimitedChange(e.target.checked)} />
          Unlimited
        </label>
      </div>

      {/* Note */}
      <div className="breakdown-form-note">
        <Input
          onChange={(e) => handleInputChange("notes", e.target.value)}
          placeholder="Write note"
          value={formInputTextData.notes}
          disabled={isCreatingNewProcedure}
        />
      </div>

      <div className="breakdown-form-buttons">
        <Button onClick={() => setShowAddProcedureFormRow(false)} className="breakdown-form-cancel-button">
          Cancel
        </Button>
        <Button type="primary" onClick={handleCreateNewBreakdownProcedure} className="breakdown-form-save-button">
          Save
        </Button>
      </div>
      </div>
    </div>
  );
}
